import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import EnhancedTable from 'components/dashboard/EnhancedTable';
import StatusBadge from 'components/dashboard/StatusBadge';
import ActionButtons from 'components/dashboard/ActionButtons';
import GlassyModal from 'components/ui/GlassyModal';
import CommentSection from 'components/dashboard/CommentSection';
import EditHistory from 'components/dashboard/EditHistory';
import ClassManagementForm from 'components/modals/ClassManagementForm';
import FloatingActionButton from 'components/ui/FloatingActionButton';
import Icon from 'components/AppIcon';
import { useClasses } from 'hooks/useClasses';
import { useAuth } from 'contexts/AuthContext';

const ClientClassesTable = ({ onMessageClick }) => {
  const { user } = useAuth();
  // Use the filter to only get current client's classes
  const { classes, loading } = useClasses({ clientId: user?.uid });
  const [selectedClass, setSelectedClass] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Status filter options for client
  const statusFilterOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'active', label: 'Active' }
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: 'class_name',
        header: 'Class Name',
        cell: ({ row }) => (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-secondary/20 rounded-lg flex items-center justify-center">
              <Icon name="GraduationCap" size={16} className="text-secondary" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-text-primary">
                {row.original.class_name || 'Untitled Class'}
              </div>
              <div className="text-sm text-text-muted">
                {row.original.subject || 'No subject specified'}
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'difficulty_level',
        header: 'Level',
        cell: ({ row }) => (
          <span className="px-2 py-1 bg-accent/10 text-accent rounded-full text-sm">
            {row.original.difficulty_level || 'Not specified'}
          </span>
        ),
      },
      {
        accessorKey: 'duration',
        header: 'Duration',
        cell: ({ row }) => (
          <span className="text-text-secondary">
            {row.original.duration || 'Not specified'}
          </span>
        ),
      },
      {
        accessorKey: 'deadline',
        header: 'Deadline',
        cell: ({ row }) => {
          const deadline = row.original.deadline;
          if (!deadline) return <span className="text-text-muted">No deadline set</span>;
          
          try {
            const date = deadline?.toDate ? deadline.toDate() : new Date(deadline);
            const isOverdue = date < new Date();
            
            return (
              <div className={`text-sm ${isOverdue ? 'text-error' : 'text-text-secondary'}`}>
                {format(date, 'MMM dd, yyyy')}
                {isOverdue && (
                  <div className="text-xs text-error">Overdue</div>
                )}
              </div>
            );
          } catch (error) {
            return <span className="text-text-muted">Invalid date</span>;
          }
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge status={row.original.status || 'pending'} />
        ),
      },
    ],
    []
  );

  const handleViewDetails = (classItem) => {
    setSelectedClass(classItem);
    setShowDetailsModal(true);
  };

  const handleSubmissionSuccess = () => {
    setShowSubmissionModal(false);
    // Classes will automatically refresh due to real-time listeners
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-secondary/20 rounded-lg flex items-center justify-center">
            <Icon name="GraduationCap" size={20} className="text-currentColor" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">My Classes</h2>
            <p className="text-text-muted text-sm">
              {classes.length} class{classes.length !== 1 ? 'es' : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowSubmissionModal(true)}
          className="hidden md:flex bg-secondary hover:bg-secondary/80 text-white px-4 py-2 rounded-lg font-medium transition-colors items-center space-x-2"
        >
          <Icon name="Plus" size={16} />
          <span>Submit New Class</span>
        </button>
      </div>

      <EnhancedTable
        data={classes}
        columns={columns}
        loading={loading}
        onRowClick={handleViewDetails}
        emptyMessage="No classes found. Contact admin to create your first class."
        mobileCardLayout={true}
        mobileColumns={['class_name', 'level', 'duration', 'deadline']}
        responsiveBreakpoint="lg"
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        statusFilterOptions={statusFilterOptions}
        hideFiltersOnMobile={true}
        mobileActions={[
          {
            label: 'View Details',
            icon: 'Eye',
            onClick: handleViewDetails,
            className: 'bg-primary/30 hover:bg-primary/40 text-white font-medium'
          },
          {
            label: 'Message',
            icon: 'MessageSquare',
            onClick: (classItem) => {
              // Navigate to messages tab and open specific conversation
              onMessageClick && onMessageClick('class', classItem.id, classItem.class_title || classItem.class_name);
            },
            className: 'bg-white/20 hover:bg-white/30 text-white'
          }
        ]}
      />

      {/* Class Details Modal */}
      <GlassyModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedClass(null);
        }}
        title={selectedClass?.class_name || 'Class Details'}
        maxWidth="max-w-3xl"
      >
        {selectedClass && (
          <div className="space-y-6">
            {/* Class Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Subject</label>
                <p className="text-text-primary">{selectedClass.subject || 'Not specified'}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Difficulty Level</label>
                <p className="text-text-primary">{selectedClass.difficulty_level || 'Not specified'}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Duration</label>
                <p className="text-text-primary">{selectedClass.duration || 'Not specified'}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Status</label>
                <StatusBadge status={selectedClass.status || 'pending'} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Deadline</label>
                <p className="text-text-primary">
                  {selectedClass.deadline ? 
                    format(selectedClass.deadline?.toDate ? selectedClass.deadline.toDate() : new Date(selectedClass.deadline), 'PPP') 
                    : 'Not set'
                  }
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Learning Objectives</label>
                <p className="text-text-primary">{selectedClass.learning_objectives || 'Not specified'}</p>
              </div>
            </div>

            {/* Requirements */}
            {selectedClass.requirements && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Requirements</label>
                <div className="bg-surface p-4 rounded-lg">
                  <p className="text-text-primary whitespace-pre-wrap">{selectedClass.requirements}</p>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            {selectedClass.additional_notes && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Additional Notes</label>
                <div className="bg-surface p-4 rounded-lg">
                  <p className="text-text-primary whitespace-pre-wrap">{selectedClass.additional_notes}</p>
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="border-t border-border pt-6">
              <CommentSection 
                itemId={selectedClass.id}
                itemType="class"
              />
            </div>

            {/* Edit History */}
            <div className="border-t border-border pt-6">
              <EditHistory assignmentId={selectedClass.id} />
            </div>
          </div>
        )}
      </GlassyModal>

      {/* Class Submission Modal */}
      <ClassManagementForm
        isOpen={showSubmissionModal}
        onClose={() => setShowSubmissionModal(false)}
        onSubmit={handleSubmissionSuccess}
      />

      {/* Mobile FAB */}
      <FloatingActionButton
        onClick={() => setShowSubmissionModal(true)}
        icon="Plus"
        label="Submit Class"
        position="bottom-right"
        size="md"
        showOnMobile={true}
        showOnDesktop={false}
      />
    </div>
  );
};

export default ClientClassesTable;